import moment from 'moment-timezone';
import range from 'lodash/range';
import sortBy from 'lodash/sortBy';
import head from 'lodash/fp/head';
import get from 'lodash/fp/get';
import getOr from 'lodash/fp/getOr';
import flow from 'lodash/fp/flow';
import join from 'lodash/fp/join';
import find from 'lodash/fp/find';
import map from 'lodash/fp/map';
import filter from 'lodash/fp/filter';
import reject from 'lodash/fp/reject';
import gt from 'lodash/fp/gt';
import entries from 'lodash/fp/entries';
import __ from 'lodash/fp/__';
import identity from 'lodash/fp/identity';
import pipe from 'lodash/fp/pipe';
import { any, prop, length } from 'ramda';
import { createSelector } from 'reselect';

import * as services from '../services/selector';
import config from '../../../config';
import {
  activeShopMinInterval,
  getShops,
  isBrandViewActive,
  getBarbers,
  getBarberUser,
  isBarberUser,
  getIsRentalBarber,
} from '../../auth/selectors';

import { TIME_TRACKING_ROLES } from '../constants';
import { SHOP_TYPE } from '../shops/constants';

export const getCollection = state => state.barbers.byId;
export const getList = state => state.barbers.list;

export const getAll = createSelector(
  [getCollection, getList],
  (barbers, list) => list.map(id => barbers[id])
);

export const getAllDeleted = state => state.barbers.deleted;

export const getAllConfirmed = flow([
  getAll,
  filter('confirmed'),
]);

export const getAllUnconfirmed = flow([
  getAll,
  reject('confirmed'),
]);

export const getHasUnconfirmedBarbers = flow(
  getAllUnconfirmed,
  length,
  gt(__, 0),
);

export const getDeletedById = (state, id) => {
  const deleted = state.barbers.deleted || [];
  return find(barber => barber.id === id, deleted);
};

export const getById = (state, id, includingDeleted = false) => {
  if (!includingDeleted) {
    return state.barbers.byId[id];
  }

  return state.barbers.byId[id] || getDeletedById(state, id);
};

export const getByIds = (state, ids) => flow([
  map(id => state.barbers.byId[id]),
  filter(identity),
])(ids);

export const getBarberServices = (state, barberId) => {
  const barber = getById(state, barberId);
  if (!barber) {
    return [];
  }
  return sortBy(services.getByIds(state, barber.services || []), service => service.order);
};

export const getBarberDurations = (state, barberId) => {
  const barber = getById(state, barberId);
  if (!barber) {
    return [];
  }
  const minInterval = activeShopMinInterval(state);
  return range(minInterval, config.maxCustomDuration + minInterval, minInterval);
};

export const getDays = state => state.barbers.availableTimes.items.map(item => ({
  day: item.day,
  available: item.times && item.times.some(time => time.works),
}));

export const getTimes = (state, day, timezone = config.defaultTimezone) => {
  const selected = state.barbers.availableTimes.items.find(item => moment(item.day).day() === moment(day).day());
  const times = selected && selected.times && selected.times.map(
    item => ({ time: moment.tz(item.time, timezone).format('HH:mm'), available: item.works })
  );
  return times || [];
};

export const getFirstBarberId = (state) => {
  const getId = get('id');
  const firstBarberId = flow(head, getId);
  return firstBarberId(getAll(state));
};

export const isTippingEnabled = getOr(true, 'tipEnabled');

export const isBarberTippingEnabled = (state, barberId) => {
  const barber = getById(state, barberId);
  return isTippingEnabled(barber);
};

export const getNextAvailableTimeKey = (barberId, duration) => join('-', [barberId, duration]);

export const getNextAvailableTimeIsFetching = (barberId, duration) =>
  getOr(false, ['barbers', 'nextAvailableTime', 'isFetching', getNextAvailableTimeKey(barberId, duration)]);

export const getPosMerchantId = flow(
  getById,
  get('posMerchantId'),
);

export const belongsToShop = (state, barberId) => state.loadingBar || state.barbers.list.includes(barberId);

export const getBarberAllowedTimeTrackRoles = flow(
  getById,
  get('timeTrackRoles'),
  entries,
  filter(([, value]) => value === true),
  map(([field]) => {
    const role = Object.entries(TIME_TRACKING_ROLES).find(([, value]) => value.field === field);
    if (role) {
      return { label: role[1].label, value: role[0] };
    }

    return null;
  }),
  filter(Boolean),
);

export const isLoadingBrandBarber = get('barbers.brandBarber.isLoading');
export const getBrandBarberId = get('barbers.brandBarber.barberId');
export const getBrandBarberShopIdList = state => getOr([], 'barbers.brandBarber.shopIds', state);
export const getBrandBarberDataByShopId = getOr({}, 'barbers.brandBarber.dataByShopId');

export const getBrandBarberShops = createSelector(
  [getBrandBarberShopIdList, getShops],
  (barberShopIds, brandShops) =>
    barberShopIds.map(id => brandShops.find(shop => shop.id === id)),
);

export const getBarberSchedules = get('barbers.barberSchedules.schedules');
export const isLoadingBarberSchedules = get('barbers.barberSchedules.isLoading');

export const getBarberDaysOnByShop = get('barbers.barberDaysOnByShop.daysOnByShop');
export const isLoadingBarberDaysOn = get('barbers.barberDaysOnByShop.isLoading');

export const getBarberDaysOffByShop = get('barbers.barberDaysOffByShop.daysOnByShop');
export const isLoadingBarberDaysOff = get('barbers.barberDaysOffByShop.isLoading');

export const getBarberPermissionPasscode = (state, barberId) => {
  if (isBrandViewActive(state)) {
    if (barberId !== getBrandBarberId(state)) {
      return '';
    }

    const barberPasscodes = Object.values(getBrandBarberDataByShopId(state))
      .map(shopData => shopData.permissionPasscode);

    const firstNonEmptyPasscode = barberPasscodes.find(passcode => !!passcode);
    return firstNonEmptyPasscode || '';
  }

  const barber = getById(state, barberId);
  return getOr('', 'permissionPasscode')(barber);
};

export const isBarberPermissionCodeLoading = get('barbers.permissionPasscodeLoadingState.isLoading');

export const isBrandBarberPermissionPasscodeEnabled = pipe(
  getBrandBarberShops,
  any(prop('permissionPasscodeEnabled'))
);

export const getUserHasRentalBarberInSameProfile = (state) => {
  if (!isBarberUser(state)) {
    return false;
  }

  if (getIsRentalBarber(state)) {
    return true;
  }

  const currentBarberId = get('barberId', getBarberUser(state));
  if (!currentBarberId) {
    return false;
  }

  return (getBarbers(state) || [])
    .filter(barber => barber.barberId === currentBarberId)
    .some(barber => barber.shop.kind === SHOP_TYPE.rental);
};
